import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';
import Box from '@mui/material/Box';
import {
  Button,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import api from '../../../services/api';
import {useGridApiRef} from '@mui/x-data-grid';
import {useStateContext} from "../../../hooks/useStateContext";
import {ChatDialog} from "../../../modules/service-provider/components/ChatDialog";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import Tooltip from '@mui/material/Tooltip';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import {CreateReservationDialog} from "../components/dialog/CreateReservationDialog";
import {Table} from "../components/salesPartner/Table";
import toast from "react-hot-toast";

interface Props {
  data: any,
}

export const Reservations: React.ComponentType<Props> = ({data}) => {
  const [reservations, setReservations] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const apiRef = useGridApiRef();
  const {user, isMobile} = useStateContext();
  const [openChat, setOpenChat] = useState<any>(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [editingReservation, setEditingReservation] = useState<any>(null);

  const {serviceReservationId} = data;

  const fetchServiceReservations = useCallback(async () => {
    setLoading(true);

    try {
      const result: any = await api.get(`/service-provider/reservations`);
      setReservations(result);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchServiceReservations();
  }, [fetchServiceReservations]);

  useEffect(() => {
    if (!serviceReservationId) {
      return;
    }

    api
      .get(`/service-provider/service-reservation-confirmations/${serviceReservationId}`)
      .then((res: any) => {
        const resData = {...res};
        resData.formatted.id = res.service_reservation_id;
        setOpenChat(resData);
      })
      .catch((e) => {
        console.log(e);
      });
  }, [serviceReservationId]);

  const handleOpenCreate = useCallback(() => {
    setEditingReservation(null);
    setOpenCreateDialog(true);
  }, []);

  const handleOpenEdit = useCallback((row: any) => {
    setEditingReservation(row);
    setOpenCreateDialog(true);
  }, []);

  const handleFinalizeRow = useCallback((row: any) => {

    api
      .post(`/sales-partner/activity-reservations/${row?.id}/confirmation`, {
        action: 'finalize'
      })
      .then((res: any) => {
        toast.success('Sikeresen véglegesítetted a foglalást');
      })
      .catch((e) => {
        console.log(e);
      }).finally(() => {
      fetchServiceReservations();
    });

  }, []);

  const handleCloseCreateDialog = useCallback(() => {
    setOpenCreateDialog(false);
    setEditingReservation(null);
  }, []);

  const handleSuccessReservation = useCallback(() => {
    handleCloseCreateDialog();
    fetchServiceReservations();
  }, [handleCloseCreateDialog, fetchServiceReservations]);

  const mapRowToInitialData = useCallback((row: any) => {
    if (!row) return undefined;

    const firstGuideReservation = Array.isArray(row.guide_reservations)
      ? row.guide_reservations[0]
      : null;

    const selectedGuide = firstGuideReservation?.guide
      ? {
        id: firstGuideReservation.guide.id,
        name: firstGuideReservation.guide.name || '',
        phone: firstGuideReservation.guide.phone || '',
      }
      : null;

    return {
      groupName: row.group?.name || row.group_name || '',
      headCount: row.group_size || '',
      date: row.date || '',
      startTime: row.start_time || '',
      selectedItem: row.activity
        ? {
          id: row.activity.id,
          name: row.activity.name,
        }
        : null,
      selectedGuide,
      guideName: selectedGuide?.name || '',
      guidePhone: selectedGuide?.phone || '',
      address:
        row.address ||
        row.address_to?.name ||
        row.address_to?.address_line1 ||
        row.venue?.name ||
        row.venue_label ||
        '',
    };
  }, []);

  return (
    <Box
      sx={{
        width: '100%',
        height: '100vh',
        overflow: 'auto',
        padding: '10px',
      }}
    >
      <Box>
        <Stack
          gap="4px"
          direction="row"
          sx={{
            marginBottom: '20px',
            '@media (max-width:600px)': {
              marginBottom: '10px',
            },
          }}
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" gap="4px" alignItems="center">
            <Typography color="#666">
              Foglalások
            </Typography>

            <Tooltip title="Foglalások frissítése">
              <IconButton
                size="small"
                onClick={fetchServiceReservations}
              >
                <RefreshRoundedIcon fontSize="small" color="primary"/>
              </IconButton>
            </Tooltip>
          </Stack>

          <Stack>
            <Button
              size="small"
              onClick={handleOpenCreate}
              startIcon={<AddRoundedIcon/>}
              variant="contained"
            >
              Új foglalás
            </Button>
          </Stack>
        </Stack>

        <Box>
          <Table
            rows={reservations}
            loading={loading}
            onLoading={setLoading}
            onRefresh={fetchServiceReservations}
            apiRef={apiRef}
            customFields={{}}
            onOpenChat={() => {
            }}
            onEditRow={handleOpenEdit}
            onFinalizeRow={handleFinalizeRow}
            editableFields={{}}
            requiredFields={{}}
          />
        </Box>
      </Box>

      {openCreateDialog && (
        <CreateReservationDialog
          open={openCreateDialog}
          onClose={handleCloseCreateDialog}
          onSuccess={() => {
            handleCloseCreateDialog();
            fetchServiceReservations();
          }}
          mode={editingReservation ? 'edit' : 'create'}
          initialData={mapRowToInitialData(editingReservation)}
          reservationId={editingReservation?.id || null}
        />
      )}

      {openChat && (
        <ChatDialog
          data={openChat}
          onClose={() => setOpenChat(false)}
        />
      )}
    </Box>
  );
};